import { useState } from "react";
import { ArrowRight, Mail } from "lucide-react";
import SectionIntro from "../components/SectionIntro";
import InfoCard from "../components/InfoCard";
import Button from "../components/Button";
import {
  careerReasons,
  clientTypes,
  company,
  founderProfile,
  homeImages,
  partnerTypes,
  workforcePipeline,
} from "../data/siteData";

export default function HomePage() {
  const [bannerImageFailed, setBannerImageFailed] = useState(false);

  return (
    <>
      <section className="home-hero">
        <div className="shell home-hero__grid">
          <div className="home-hero__content">
            <p className="eyebrow">STEM Education & Clinical Research Workforce Development</p>
            <h1>{company.heroTitle}</h1>
            <p className="lead">{company.heroDescription}</p>
            <div className="button-row">
              <Button to="/services">Learn More</Button>
              <Button to="/partnerships" variant="teal">
                Partner With Us
              </Button>
              <Button to="/contact#contact-form" variant="secondary">
                Join Interest List
              </Button>
            </div>
          </div>

          <div className="home-hero__visual">
            <img src={homeImages.hero} alt="Clinical research professionals working in a laboratory" />
            <div className="hero-ribbon">
              <div>
                <h3>Advancing science. Empowering minds.</h3>
                <p>Stronger workforce. Stronger research. Stronger communities.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section shell what-we-do-section">
        <SectionIntro
          eyebrow="What We Do"
          title="STEM education, clinical research training, workforce development, and scientific consulting"
          align="center"
        />
        <div className="card-grid card-grid--three top-gap-lg">
          {clientTypes.map(item => (
            <InfoCard key={item.title} icon={item.icon} title={item.title} text={item.description} />
          ))}
        </div>
      </section>

      <section className="section shell leadership-overview section--tight-top">
        <div className="founder-mini-card">
          <div className="founder-mini-card__image-wrap">
            <img src={founderProfile.image} alt={founderProfile.name} loading="lazy" />
          </div>
          <div>
            <p className="eyebrow">
              {founderProfile.role}
              {founderProfile.credentials ? ` · ${founderProfile.credentials}` : ""}
            </p>
            <h2>{founderProfile.name}</h2>
            <p className="founder-profile__role">{founderProfile.specialty}</p>
            <ul className="compact-list">
              {founderProfile.quickFacts.map(fact => (
                <li key={fact}>{fact}</li>
              ))}
            </ul>
            <Button to="/about#founder-scientific-leadership" variant="link">
              Learn More About Dr. Smith Wade <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>

      <section className="section shell pipeline-section section--tight-top">
        <SectionIntro
          eyebrow="Our Workforce Development Pipeline"
          title="A clear path from training to career readiness"
          align="center"
        />
        <div className="pipeline pipeline--five top-gap-md">
          {workforcePipeline.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="pipeline-step">
                <div className={`pipeline-step__icon ${index === 3 ? "pipeline-step__icon--orange" : ""}`}>
                  <Icon size={22} />
                </div>
                <strong>
                  {index + 1}. {item.title}
                </strong>
                <p>{item.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="section section--blue why-section">
        <div className="shell split-layout split-layout--balanced">
          <div>
            <p className="eyebrow eyebrow--light">Why Clinical Research Careers?</p>
            <h2>Meaningful careers that support medical discovery and patient impact.</h2>
          </div>
          <div className="icon-stat-grid">
            {careerReasons.map(item => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="icon-stat">
                  <Icon size={30} />
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section shell section--tight-top">
        <div className="feature-banner">
          <div>
            <p className="eyebrow">Our Partners</p>
            <h2>Working together to build the future of clinical research.</h2>
            <p>
              BrainTorch™ Bio partners with education, healthcare, industry, workforce, and community organizations to
              expand opportunity and build stronger talent pipelines.
            </p>
            <div className="partner-icon-row" aria-label="Partner categories">
              {partnerTypes.map(item => {
                const Icon = item.icon;
                return <Icon key={item.title} size={34} />;
              })}
            </div>
            <div className="button-row top-gap-md">
              <Button to="/partnerships">Partner With Us</Button>
              <Button to="/workforce-development" variant="secondary">
                Explore the Pipeline
              </Button>
            </div>
          </div>

          {!bannerImageFailed ? (
            <img
              src={homeImages.wide}
              alt="Clinical research team in a laboratory"
              className="feature-banner__image"
              loading="lazy"
              onError={() => setBannerImageFailed(true)}
            />
          ) : (
            <div className="feature-banner__image feature-banner__image--placeholder" aria-hidden="true" />
          )}
        </div>
      </section>

      <section className="stay-connected">
        <div className="shell stay-connected__grid">
          <div className="stay-connected__intro">
            <div className="stay-connected__icon">
              <Mail size={28} />
            </div>
            <div>
              <h2>Stay Connected</h2>
              <p>Join our interest list to receive updates on programs, events, and opportunities.</p>
            </div>
          </div>
          <div className="button-row" style={{ justifyContent: "center" }}>
            <Button to="/contact#contact-form" variant="teal">
              Join Interest List
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
